import { useEffect, useState } from 'react';
import { useGenerateSSOToken } from 'apis/public/onboardingForms/hooks';

const loadFeatureBaseSDK = (): Promise<void> => {
  return new Promise((resolve) => {
    const win = window as any;
    if (win.Featurebase) {
      resolve();
      return;
    }

    let script = document.getElementById('featurebase-sdk') as HTMLScriptElement;
    if (!script) {
      script = document.createElement('script');
      script.id = 'featurebase-sdk';
      script.src = 'https://do.featurebase.app/js/sdk.js';
      script.async = true;
      document.head.appendChild(script);
    }

    const checkInterval = setInterval(() => {
      if (win.Featurebase) {
        clearInterval(checkInterval);
        resolve();
      }
    }, 100);

    setTimeout(() => {
      clearInterval(checkInterval);
      resolve();
    }, 10000);
  });
};

const clearFeaturebaseWidgets = () => {
  const win = window as any;
  if (win.Featurebase) {
    try {
      win.Featurebase('destroy');
    } catch (error) {
      console.warn('Error destroying Featurebase widgets:', error);
    }
  }

  const featurebaseElements = document.querySelectorAll(
    '[data-featurebase], [id*="featurebase"], [class*="featurebase"]'
  );
  featurebaseElements.forEach((element) => element.remove());

  const featurebaseIframes = document.querySelectorAll('iframe[src*="featurebase"]');
  featurebaseIframes.forEach((element) => element.remove());
};

const FeaturebaseWidget = () => {
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const { data: ssoData, isLoading: isApiLoading, error: apiError } = useGenerateSSOToken();

  useEffect(() => {
    const loadSDK = async () => {
      await loadFeatureBaseSDK();
      setSdkLoaded(true);
    };
    loadSDK();

    return () => {
      clearFeaturebaseWidgets();
    };
  }, []);

  useEffect(() => {
    if (!sdkLoaded || isApiLoading || !ssoData) return;

    const win = window as any;
    const { token: ssoSupportToken, identity } = ssoData;

    if (!ssoSupportToken) {
      clearFeaturebaseWidgets();
      return;
    }

    try {
      // Boot Featurebase first (required)
      win.Featurebase('boot', {
        appId: '68430292a62bfc928f502d6c',
        theme: 'light',
        language: 'en',
        featurebaseJwt: ssoSupportToken
      });

      // Identify user with SSO data
      win.Featurebase('identify', {
        organization: 'supportkickcall',
        userId: identity?.user_id,
        email: identity?.email,
        name: identity?.name,
        featurebaseJwt: ssoSupportToken,
        company: {
          id: identity?.company_id,
          name: identity?.company_name
        }
      });

      // Initialize survey widget
      win.Featurebase(
        'initialize_survey_widget',
        {
          organization: 'supportkickcall',
          placement: 'bottom-right',
          theme: 'light',
          locale: 'en',
          featurebaseJwt: ssoSupportToken
        },
        (err: any) => {
          if (err) {
            console.error('Featurebase survey widget error:', err);
          }
        }
      );
    } catch (error) {
      console.error('Error initializing Featurebase widgets:', error);
    }
  }, [sdkLoaded, ssoData, isApiLoading]);

  if (isApiLoading) {
    return <div data-featurebase-embed className='featurebase-loading' />;
  }

  if (apiError) {
    console.warn('Featurebase widget failed to load:', apiError);
  }

  return <div data-featurebase-embed />;
};

export default FeaturebaseWidget;
